import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import React, { memo, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { WorkflowType } from '@/constants/dispositions';
import { useStore } from '@/store/store';
import {
  type AssigneeUpdatePlan,
  categorizeNonAssignableCases,
  getWrongStateCasesDescription,
  groupCasesByCurrentAssignee,
  separateAssignableCases,
} from '@/utils/assigneeConfirmation';

interface AssigneeConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (note?: string) => void;
  assigneeUpdatePlan: AssigneeUpdatePlan | null;
  cases?: GetCaseRecordResponse[];
  isLoading?: boolean;
  isIndividual?: boolean;
}

const AssigneeConfirmationDialogComponent = ({
  isOpen,
  onClose,
  onConfirm,
  assigneeUpdatePlan,
  cases = [],
  isLoading = false,
  isIndividual = false,
}: AssigneeConfirmationDialogProps) => {
  const [note, setNote] = useState('');
  const selectedWorkflow = useStore(state => state.getSelectedWorkflow());

  // Hide notes for PQC workflow
  const showNotes = selectedWorkflow?.workflow_type !== WorkflowType.PQC;

  const handleClose = () => {
    setNote('');
    onClose();
  };

  const handleConfirm = () => {
    onConfirm(note.trim() || undefined);
    setNote('');
  };

  // Memoize the processed cases to avoid recalculation on every render
  const processedCases = useMemo(() => {
    if (!assigneeUpdatePlan) return null;

    const { assignableCases, nonAssignableCases } = separateAssignableCases(
      cases,
      assigneeUpdatePlan,
    );
    const assigneeGroups = groupCasesByCurrentAssignee(assignableCases);
    const { wrongStateCases, alreadyAssignedCases } = categorizeNonAssignableCases(
      nonAssignableCases,
      assigneeUpdatePlan.assigneeUserId,
    );

    return {
      assignableCases,
      nonAssignableCases,
      assigneeGroups,
      wrongStateCases,
      alreadyAssignedCases,
    };
  }, [assigneeUpdatePlan, cases]);

  if (!assigneeUpdatePlan || !processedCases) return null;

  const { assigneeUserId, count } = assigneeUpdatePlan;
  const { assigneeGroups, nonAssignableCases, wrongStateCases, alreadyAssignedCases } =
    processedCases;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Confirm Change</DialogTitle>
          <DialogDescription>
            {isIndividual
              ? `Are you sure you want to assign this case to ${assigneeUserId}?`
              : `You are about to change the assignee for ${count} case${count === 1 ? '' : 's'}.`}
          </DialogDescription>
        </DialogHeader>

        {/* Show detailed cards for bulk operations only */}
        {!isIndividual && (
          <div className="space-y-4 py-4">
            {/* Assignable cases grouped by current assignee */}
            {Object.entries(assigneeGroups).map(([fromAssignee, groupCases]) => (
              <div key={fromAssignee} className="border rounded-lg p-4 space-y-3">
                {/* Header section showing current assignee and case count */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">{fromAssignee}</span>
                  <span className="text-sm text-muted-foreground font-medium">
                    {groupCases.length} case{groupCases.length === 1 ? '' : 's'}
                  </span>
                </div>

                {/* Assignment change section */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground font-medium">From:</span>
                      <span className="text-muted-foreground py-1 rounded-md text-sm font-medium break-all">
                        {fromAssignee}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground font-medium">To:</span>
                      <span className="text-sm font-medium text-foreground break-all">
                        {assigneeUserId}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Warning section for non-assignable cases */}
            {nonAssignableCases.length > 0 && (
              <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                <div className="flex items-start gap-3">
                  <div className="text-orange-600 mt-0.5">⚠️</div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-orange-800 mb-1">
                      {nonAssignableCases.length} case
                      {nonAssignableCases.length === 1 ? '' : 's'} will be skipped
                    </div>
                    <div className="text-xs text-orange-700 space-y-1">
                      {wrongStateCases.length > 0 && (
                        <div>{getWrongStateCasesDescription(wrongStateCases)}</div>
                      )}
                      {alreadyAssignedCases.length > 0 && (
                        <div>
                          {alreadyAssignedCases.length} case
                          {alreadyAssignedCases.length === 1 ? ' is' : 's are'} already assigned to{' '}
                          {assigneeUserId}.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Note input - hidden for PQC workflow */}
        {showNotes && (
          <div className="space-y-2">
            <Label htmlFor="assignee-note" className="text-sm text-muted-foreground">
              Add a note (optional)
            </Label>
            <Textarea
              id="assignee-note"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Add context about this assignee change..."
              className="min-h-[80px] resize-none"
              disabled={isLoading}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Updating...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const AssigneeConfirmationDialog = memo(AssigneeConfirmationDialogComponent);
